import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeSearches, dedupeCaseInsensitive } from '../utils/deduplication';

const SAVED_SEARCHES_GUEST_KEY = 'ff_saved_work_searches';
const USER_SAVED_SEARCHES_FIELD = 'savedSearches';
const userSavedSearchesCacheKey = (uid) => `ff_saved_work_searches_${uid}`;

function loadGuestSavedSearches() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SAVED_SEARCHES_GUEST_KEY) || '[]');
    return normalizeSearches(parsed);
  } catch {
    return [];
  }
}

function saveGuestSavedSearches(searches) {
  try {
    localStorage.setItem(SAVED_SEARCHES_GUEST_KEY, JSON.stringify(normalizeSearches(searches)));
  } catch {}
}

function loadUserCacheSavedSearches(uid) {
  if (!uid) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(userSavedSearchesCacheKey(uid)) || '[]');
    return normalizeSearches(parsed);
  } catch {
    return [];
  }
}

function saveUserCacheSavedSearches(uid, searches) {
  if (!uid) return;
  try {
    localStorage.setItem(userSavedSearchesCacheKey(uid), JSON.stringify(normalizeSearches(searches)));
  } catch {}
}

async function loadUserSavedSearches(uid) {
  if (!uid) return [];
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return [];
    const data = snap.data();
    return normalizeSearches(data?.[USER_SAVED_SEARCHES_FIELD]);
  } catch {
    return [];
  }
}

async function persistUserSavedSearches(uid, searches) {
  if (!uid) return;
  await setDoc(doc(db, 'users', uid), { [USER_SAVED_SEARCHES_FIELD]: normalizeSearches(searches) }, { merge: true });
}

/**
 * Manages saved work-location searches with guest localStorage ↔ user Firestore sync.
 * Handles loading, saving, merging guest→user on sign-in, and modal open/close state.
 */
export function useSavedSearches({ user, authLoading }) {
  const [savedSearches, setSavedSearches] = useState(() => loadGuestSavedSearches());
  const [savedSearchesOpen, setSavedSearchesOpen] = useState(false);

  // Escape key + body scroll lock for modal
  useEffect(() => {
    if (!savedSearchesOpen) return;
    function handleEscape(e) {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') setSavedSearchesOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [savedSearchesOpen]);

  // Close modal on sign-out
  useEffect(() => {
    if (!savedSearchesOpen) return;
    if (!user?.uid) setSavedSearchesOpen(false);
  }, [savedSearchesOpen, user?.uid]);

  // Load/sync saved searches for this user (or fall back to guest localStorage).
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (authLoading) return;

      if (!user?.uid) {
        if (!cancelled) setSavedSearches(loadGuestSavedSearches());
        return;
      }

      const uid = user.uid;

      // Optimistic: show cached user searches immediately.
      const cached = loadUserCacheSavedSearches(uid);
      if (!cancelled && cached.length) setSavedSearches(cached);

      const guestSaved = loadGuestSavedSearches();
      const remoteSaved = await loadUserSavedSearches(uid);
      if (cancelled) return;

      const merged = guestSaved.length
        ? dedupeCaseInsensitive([...guestSaved, ...remoteSaved])
        : remoteSaved;
      const next = normalizeSearches(merged);

      setSavedSearches(next);
      saveUserCacheSavedSearches(uid, next);

      // Migrate guest saved searches into the account once, so they sync across devices.
      if (guestSaved.length) {
        await persistUserSavedSearches(uid, next);
        try {
          localStorage.removeItem(SAVED_SEARCHES_GUEST_KEY);
        } catch {}
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, authLoading]);

  const saveCurrentSearch = useCallback((currentAddress) => {
    const trimmed = (currentAddress || '').trim();
    if (!trimmed) return;
    const next = [trimmed, ...savedSearches.filter((s) => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 30);
    setSavedSearches(next);
    if (!user?.uid) {
      saveGuestSavedSearches(next);
      return;
    }
    saveUserCacheSavedSearches(user.uid, next);
    persistUserSavedSearches(user.uid, next).catch(() => {});
  }, [savedSearches, user?.uid]);

  return {
    savedSearches,
    savedSearchesOpen,
    setSavedSearchesOpen,
    saveCurrentSearch,
  };
}
